import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { seedAccommodations, typeLabels, typeColors, typeImages, Accommodation } from "@/mocks/accommodations";
import FeaturedSlider from "@/pages/accommodations/components/FeaturedSlider";
import ReservationModal from "@/pages/accommodations/components/ReservationModal";

const REGIONS = [
  { label: "Tümü", value: "all" },
  { label: "Karadeniz", value: "karadeniz", cities: ["Trabzon", "Rize", "Artvin", "Giresun", "Sinop", "Samsun", "Ordu", "Zonguldak", "Kastamonu", "Bartın", "Karabük"] },
  { label: "Ege", value: "ege", cities: ["İzmir", "Muğla (Bodrum/Marmaris)", "Aydın", "Denizli", "Manisa", "Balıkesir", "Çanakkale", "Uşak", "Kütahya", "Afyonkarahisar"] },
  { label: "Akdeniz", value: "akdeniz", cities: ["Antalya", "Mersin", "Adana", "Hatay", "Isparta", "Burdur", "Kahramanmaraş", "Osmaniye"] },
  { label: "İç Anadolu", value: "ic-anadolu", cities: ["Nevşehir (Kapadokya)", "Ankara", "Konya", "Kayseri", "Sivas", "Yozgat", "Kırşehir", "Aksaray", "Niğde", "Karaman"] },
  { label: "Doğu Anadolu", value: "dogu-anadolu", cities: ["Erzurum", "Van", "Ağrı", "Kars", "Iğdır", "Ardahan", "Erzincan", "Malatya", "Elazığ", "Bingöl", "Muş", "Bitlis", "Hakkari"] },
  { label: "Güneydoğu", value: "guneydogu", cities: ["Mardin", "Gaziantep", "Şanlıurfa", "Diyarbakır", "Batman", "Siirt", "Şırnak", "Adıyaman", "Kilis"] },
  { label: "Marmara", value: "marmara", cities: ["İstanbul", "Bursa", "Edirne", "Tekirdağ", "Kırklareli", "Sakarya", "Kocaeli", "Yalova", "Bilecik", "Bolu", "Düzce", "Sakarya"] },
];

const ALL_AMENITIES = [
  "Ücretsiz WiFi", "Otopark", "Havuz", "Spa & Masaj", "Restoran", "Bar",
  "Kahvaltı Dahil", "Klima", "Balkon / Teras", "Deniz Manzarası",
  "Dağ Manzarası", "Evcil Hayvan Kabul", "Engelli Erişimi", "Fitness Salonu",
  "Transfer Hizmeti", "Bisiklet Kiralama",
];

const PRICE_RANGES = [
  { label: "Tümü", value: "all" },
  { label: "₺ Ekonomik", value: "ekonomik" },
  { label: "₺₺ Orta", value: "orta" },
  { label: "₺₺₺ Üst Segment", value: "ust" },
  { label: "₺₺₺₺ Lüks", value: "luks" },
];

const priceMatch = (priceRange: string, filter: string) => {
  if (filter === "all") return true;
  const p = priceRange.toLowerCase();
  if (filter === "ekonomik") return p.startsWith("₺ ") || p.startsWith("₺(");
  if (filter === "orta") return p.startsWith("₺₺ ") || p.startsWith("₺₺(");
  if (filter === "ust") return p.startsWith("₺₺₺ ") || p.startsWith("₺₺₺(");
  if (filter === "luks") return p.startsWith("₺₺₺₺");
  return true;
};

interface AdvancedFilterPanelProps {
  selectedAmenities: string[];
  onAmenitiesChange: (a: string[]) => void;
  selectedPrice: string;
  onPriceChange: (p: string) => void;
  onReset: () => void;
  activeCount: number;
}

const AdvancedFilterPanel = ({
  selectedAmenities, onAmenitiesChange,
  selectedPrice, onPriceChange,
  onReset, activeCount,
}: AdvancedFilterPanelProps) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggleAmenity = (a: string) => {
    onAmenitiesChange(
      selectedAmenities.includes(a)
        ? selectedAmenities.filter((x) => x !== a)
        : [...selectedAmenities, a]
    );
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
          activeCount > 0
            ? "bg-stone-900 text-white border-stone-900"
            : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
        }`}
      >
        <i className="ri-equalizer-2-line"></i>
        Filtrele
        {activeCount > 0 && (
          <span className="bg-amber-400 text-stone-900 text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
            {activeCount}
          </span>
        )}
        {open ? <i className="ri-arrow-up-s-line text-xs"></i> : <i className="ri-arrow-down-s-line text-xs"></i>}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-stone-100 rounded-2xl z-30 overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}>
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h4 className="font-bold text-stone-900 text-sm">Gelişmiş Filtreler</h4>
            {activeCount > 0 && (
              <button onClick={onReset} className="text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer whitespace-nowrap">
                Temizle ({activeCount})
              </button>
            )}
          </div>

          <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Price Range */}
            <div>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Fiyat Aralığı</p>
              <div className="space-y-2">
                {PRICE_RANGES.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => onPriceChange(value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer text-left ${
                      selectedPrice === value
                        ? "bg-amber-50 border border-amber-300 text-amber-800 font-semibold"
                        : "bg-stone-50 border border-transparent text-stone-600 hover:bg-stone-100"
                    }`}
                  >
                    <div className={`w-4 h-4 flex items-center justify-center rounded-full border-2 flex-shrink-0 ${
                      selectedPrice === value ? "border-amber-500 bg-amber-500" : "border-stone-300"
                    }`}>
                      {selectedPrice === value && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                    </div>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                Olanaklar
                {selectedAmenities.length > 0 && (
                  <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                    {selectedAmenities.length} seçili
                  </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {ALL_AMENITIES.map((amenity) => {
                  const checked = selectedAmenities.includes(amenity);
                  return (
                    <button
                      key={amenity}
                      onClick={() => toggleAmenity(amenity)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-all cursor-pointer text-left ${
                        checked
                          ? "bg-amber-50 border border-amber-300 text-amber-800 font-semibold"
                          : "bg-stone-50 border border-transparent text-stone-600 hover:bg-stone-100"
                      }`}
                    >
                      <div className={`w-4 h-4 flex items-center justify-center rounded flex-shrink-0 border transition-all ${
                        checked ? "bg-amber-500 border-amber-500" : "border-stone-300"
                      }`}>
                        {checked && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="leading-tight">{amenity}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-5 py-4 border-t border-stone-100">
            <button
              onClick={() => setOpen(false)}
              className="w-full py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Filtreleri Uygula
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Contact Modal ─────────────────────────────────────────────
interface ContactModalProps {
  accommodation: Accommodation;
  onClose: () => void;
}

const ContactModal = ({ accommodation, onClose }: ContactModalProps) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", checkInDate: "", checkOutDate: "", guests: "2", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const body = new URLSearchParams();
    body.append("businessName", accommodation.businessName);
    body.append("name", formData.name);
    body.append("email", formData.email);
    body.append("phone", formData.phone);
    body.append("checkInDate", formData.checkInDate);
    body.append("checkOutDate", formData.checkOutDate);
    body.append("guests", formData.guests);
    body.append("message", formData.message);
    try {
      await fetch("https://readdy.ai/api/form/d7d5bpskpdh0a4advrl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch { /* silent */ }
    setTimeout(() => { setSending(false); setSent(true); }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h3 className="font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {accommodation.businessName} ile İletişim
            </h3>
            <p className="text-xs text-stone-400 mt-0.5">{accommodation.city} · {accommodation.district}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-stone-100 hover:bg-stone-200 rounded-full cursor-pointer transition-colors">
            <i className="ri-close-line text-stone-600"></i>
          </button>
        </div>
        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-emerald-100 rounded-full">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
            </div>
            <h4 className="font-bold text-stone-900 mb-2">Mesajınız İletildi!</h4>
            <p className="text-stone-500 text-sm"><strong>{accommodation.businessName}</strong> en kısa sürede size geri dönecektir.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap">Kapat</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-readdy-form>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Adınız Soyadınız *</label>
                <input type="text" name="name" required value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ad Soyad" className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-posta *</label>
                <input type="email" name="email" required value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  placeholder="email@ornek.com" className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Telefon</label>
                <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="0532 000 00 00" className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Misafir Sayısı</label>
                <select name="guests" value={formData.guests} onChange={(e) => setFormData((p) => ({ ...p, guests: e.target.value }))}
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white">
                  {["1","2","3","4","5","6+"].map((n) => <option key={n} value={n}>{n} Kişi</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Giriş Tarihi</label>
                <input type="date" name="checkInDate" value={formData.checkInDate} onChange={(e) => setFormData((p) => ({ ...p, checkInDate: e.target.value }))}
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Çıkış Tarihi</label>
                <input type="date" name="checkOutDate" value={formData.checkOutDate} onChange={(e) => setFormData((p) => ({ ...p, checkOutDate: e.target.value }))}
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">Mesajınız</label>
              <textarea name="message" rows={3} maxLength={500} value={formData.message} onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                placeholder="Sorularınızı veya özel isteklerinizi yazın..."
                className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none" />
              <p className="text-xs text-stone-400 text-right mt-1">{formData.message.length}/500</p>
            </div>
            <button type="submit" disabled={sending}
              className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap">
              {sending ? <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</> : <><i className="ri-send-plane-line"></i> Mesaj Gönder</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Map View ──────────────────────────────────────────────────
interface MapViewProps {
  accommodations: Accommodation[];
  selected: Accommodation | null;
  onSelect: (acc: Accommodation) => void;
  onContact: (acc: Accommodation) => void;
}

const MapView = ({ accommodations, selected, onSelect, onContact }: MapViewProps) => {
  const withCoords = accommodations.filter((a) => a.lat && a.lng);
  const mapCenter = selected?.lat
    ? `${selected.lat},${selected.lng}`
    : "39.0,35.0";
  const zoom = selected ? 13 : 6;

  const mapQuery = withCoords.length > 0
    ? encodeURIComponent(withCoords.map((a) => a.businessName + " " + a.address).join("|"))
    : encodeURIComponent("Turkey");

  return (
    <div className="flex gap-5" style={{ height: "600px" }}>
      {/* Left: Pin List */}
      <div className="w-80 flex-shrink-0 flex flex-col bg-white border border-stone-100 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 flex-shrink-0">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
            {accommodations.length} İşletme
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {accommodations.map((acc, idx) => (
            <button
              key={acc.id}
              onClick={() => onSelect(acc)}
              className={`w-full text-left px-4 py-3 border-b border-stone-50 transition-all cursor-pointer flex items-start gap-3 ${
                selected?.id === acc.id ? "bg-amber-50 border-l-2 border-l-amber-500" : "hover:bg-stone-50"
              }`}
            >
              {/* Number pin */}
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 mt-0.5 ${
                selected?.id === acc.id ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-600"
              }`}>
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-900 text-sm leading-snug line-clamp-1">{acc.businessName}</p>
                <p className="text-xs text-stone-400 mt-0.5">{acc.city} · {acc.district}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[acc.businessType] || "bg-stone-100 text-stone-600"}`}>
                    {typeLabels[acc.businessType] || acc.businessType}
                  </span>
                  <span className="text-xs font-bold text-amber-600">{acc.priceRange.split(" ")[0]}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Map + Info Card */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Map iframe */}
        <div className="flex-1 rounded-2xl overflow-hidden border border-stone-100">
          <iframe
            key={`${mapCenter}-${zoom}`}
            title="Konaklama Haritası"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={
              selected?.lat
                ? `https://maps.google.com/maps?q=${encodeURIComponent(selected.businessName + " " + selected.address)}&z=${zoom}&output=embed`
                : `https://maps.google.com/maps?q=${mapQuery}&z=${zoom}&output=embed`
            }
          ></iframe>
        </div>

        {/* Selected info card */}
        {selected && (
          <div className="bg-white border border-amber-200 rounded-2xl p-4 flex items-center gap-4 flex-shrink-0">
            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src={(typeImages[selected.businessType] || typeImages.hotel)[0]}
                alt={selected.businessName}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[selected.businessType] || "bg-stone-100 text-stone-600"}`}>
                  {typeLabels[selected.businessType]}
                </span>
                {selected.starRating && (
                  <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold">
                    <i className="ri-star-fill text-amber-400"></i> {selected.starRating} Yıldız
                  </span>
                )}
              </div>
              <h3 className="font-bold text-stone-900 text-sm leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                {selected.businessName}
              </h3>
              <p className="text-xs text-stone-400 mt-0.5">{selected.address}</p>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <Link
                to={`/accommodation/${selected.id}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-stone-900 text-white text-xs font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line"></i> Detay
              </Link>
              <button
                onClick={() => onContact(selected)}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line"></i> Rezervasyon
              </button>
            </div>
          </div>
        )}

        {!selected && (
          <div className="bg-stone-50 rounded-2xl p-4 text-center flex-shrink-0">
            <p className="text-stone-400 text-sm">
              <i className="ri-map-pin-line text-amber-400 mr-1"></i>
              Detayları görmek için sol listeden bir işletme seçin
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const ITEMS_PER_PAGE = 9;

const AccommodationsPage = () => {
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("Tümü");
  const [filterCity, setFilterCity] = useState("Tümü");
  const [filterPrice, setFilterPrice] = useState("all");
  const [filterAmenities, setFilterAmenities] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [filterNewOnly, setFilterNewOnly] = useState(false);
  const [filterRegion, setFilterRegion] = useState("all");
  const [contactModal, setContactModal] = useState<Accommodation | null>(null);
  const [reservationModal, setReservationModal] = useState<Accommodation | null>(null);
  const [mapSelected, setMapSelected] = useState<Accommodation | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const advancedFilterCount = (filterPrice !== "all" ? 1 : 0) + filterAmenities.length;

  const resetAdvancedFilters = () => {
    setFilterPrice("all");
    setFilterAmenities([]);
  };

  const regionCities = filterRegion !== "all"
    ? REGIONS.find((r) => r.value === filterRegion)?.cities || []
    : [];

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterType, filterCity, filterPrice, filterAmenities, filterNewOnly, filterRegion]);

  useEffect(() => {
    const title = "Türkiye Konaklama Rehberi — Otel, Pansiyon, Bungalov ve Kamp Alanları";
    const desc = "Türkiye'nin dört bir yanından onaylı otel, pansiyon, butik otel, bungalov ve kamp alanları. Karadeniz yaylalarından Ege kıyılarına, Akdeniz'den İç Anadolu'ya konaklama rehberi.";
    document.title = title;
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    const setPaginationLinks = (page: number, total: number) => {
      ["prev", "next"].forEach((rel) => {
        const existing = document.querySelector(`link[rel="${rel}"]`);
        if (existing) existing.remove();
      });
      const base = "https://geziturkiyem.com/accommodations";
      if (page > 1) {
        const prev = document.createElement("link");
        prev.setAttribute("rel", "prev");
        prev.setAttribute("href", page === 2 ? base : `${base}?page=${page - 1}`);
        document.head.appendChild(prev);
      }
      if (page < total) {
        const next = document.createElement("link");
        next.setAttribute("rel", "next");
        next.setAttribute("href", `${base}?page=${page + 1}`);
        document.head.appendChild(next);
      }
    };
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.setAttribute("type", "application/ld+json"); document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };

    const canonicalUrl = currentPage === 1
      ? "https://geziturkiyem.com/accommodations"
      : `https://geziturkiyem.com/accommodations?page=${currentPage}`;
    setCanonical(canonicalUrl);
    setPaginationLinks(currentPage, totalPages);
    setMeta("description", desc);
    setMeta("keywords", "türkiye konaklama, otel, pansiyon, bungalov, kamp alanı, butik otel, karadeniz konaklama, ege otelleri, akdeniz tatil");
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://geziturkiyem.com/accommodations", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);

    injectSchema("schema-accommodations-page", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": title,
      "description": desc,
      "url": "https://geziturkiyem.com/accommodations",
      "inLanguage": "tr-TR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
          { "@type": "ListItem", "position": 2, "name": "Konaklama", "item": "https://geziturkiyem.com/accommodations" }
        ]
      }
    });

    return () => {
      document.title = "Türkiye Seyahat Rehberi";
      ["prev", "next"].forEach((rel) => {
        const el = document.querySelector(`link[rel="${rel}"]`);
        if (el) el.remove();
      });
    };
  }, [currentPage, totalPages]);

  useEffect(() => {
    const approved = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
      .filter((a: Accommodation) => a.status === "approved");
    setAccommodations([...seedAccommodations, ...approved]);
  }, []);

  const types = ["Tümü", "Otel", "Pansiyon", "Butik Otel", "Apart", "Kamp & Glamping", "Bungalov", "Karavan & Kamp", "Turizm Firması"];
  const cities = ["Tümü", ...Array.from(new Set(accommodations.map((a) => a.city)))];

  const newMemberCount = accommodations.filter((a) =>
    a.appliedAt && (Date.now() - new Date(a.appliedAt).getTime()) < 30 * 24 * 60 * 60 * 1000
  ).length;

  const filtered = accommodations.filter((a) => {
    const matchSearch = !search
      || a.businessName.toLowerCase().includes(search.toLowerCase())
      || a.city.toLowerCase().includes(search.toLowerCase())
      || a.shortDesc.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === "Tümü" || typeLabels[a.businessType] === filterType;
    const matchCity = filterCity === "Tümü" || a.city === filterCity;
    const matchPrice = priceMatch(a.priceRange, filterPrice);
    const matchAmenities = filterAmenities.length === 0
      || filterAmenities.every((am) => a.amenities?.includes(am));
    const matchNew = !filterNewOnly
      || (a.appliedAt && (Date.now() - new Date(a.appliedAt).getTime()) < 30 * 24 * 60 * 60 * 1000);
    const matchRegion = filterRegion === "all" || regionCities.some((c) => a.city.includes(c) || c.includes(a.city));
    return matchSearch && matchType && matchCity && matchPrice && matchAmenities && matchNew && matchRegion;
  }).sort((a, b) => {
    if (a.membershipTier === "gold" && b.membershipTier !== "gold") return -1;
    if (a.membershipTier !== "gold" && b.membershipTier === "gold") return 1;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedAccommodations = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Gold items for current page
  const goldItems = paginatedAccommodations.filter((a) => a.membershipTier === "gold");
  const regularItems = paginatedAccommodations.filter((a) => a.membershipTier !== "gold");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-16 bg-stone-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Turkey%20hotel%20panoramic%20view%20Bosphorus%20Istanbul%20luxury%20accommodation%20scenic%20landscape%20beautiful%20warm%20golden%20light&width=1400&height=500&seq=acc-hero-main&orientation=landscape"
            alt="Konaklama Rehberi"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
            Konaklama Rehberi
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Otel, Pansiyon & Turizm Firmaları
          </h1>
          <p className="text-stone-300 text-sm max-w-xl mx-auto mb-8">
            Türkiye'nin dört bir yanından onaylı konaklama işletmeleri. Doğrudan form üzerinden iletişime geçin.
          </p>
          <div className="max-w-lg mx-auto relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-stone-400"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="İşletme adı veya şehir ara..."
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-stone-400 rounded-2xl pl-11 pr-4 py-3.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Featured Slider */}
        <FeaturedSlider />

        {/* Bölge Filtresi */}
        <div className="flex gap-2 flex-wrap mb-4">
          {REGIONS.map((r) => (
            <button
              key={r.value}
              onClick={() => { setFilterRegion(r.value); setFilterCity("Tümü"); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                filterRegion === r.value
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
              }`}
            >
              {r.value === "karadeniz" && <i className="ri-water-flash-line text-xs"></i>}
              {r.value === "ege" && <i className="ri-ship-line text-xs"></i>}
              {r.value === "akdeniz" && <i className="ri-sun-line text-xs"></i>}
              {r.value === "ic-anadolu" && <i className="ri-landscape-line text-xs"></i>}
              {r.value === "dogu-anadolu" && <i className="ri-landscape-line text-xs"></i>}
              {r.value === "guneydogu" && <i className="ri-ancient-gate-line text-xs"></i>}
              {r.value === "marmara" && <i className="ri-building-line text-xs"></i>}
              {r.value === "all" && <i className="ri-map-line text-xs"></i>}
              {r.label}
            </button>
          ))}
        </div>

        {/* Filters + View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex gap-2 flex-wrap flex-1">
            {types.map((t) => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filterType === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {/* New Members Toggle */}
            <button
              onClick={() => setFilterNewOnly((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                filterNewOnly
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : "bg-white text-stone-700 border-stone-200 hover:border-emerald-300 hover:text-emerald-700"
              }`}
            >
              <i className="ri-sparkling-line"></i>
              Yeni Üyeler
              {newMemberCount > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${filterNewOnly ? "bg-white/30 text-white" : "bg-emerald-100 text-emerald-700"}`}>
                  {newMemberCount}
                </span>
              )}
            </button>
            <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
              className="border border-stone-200 rounded-xl px-4 py-2 text-sm text-stone-700 outline-none focus:border-amber-400 bg-white cursor-pointer">
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {/* Advanced Filter */}
            <AdvancedFilterPanel
              selectedAmenities={filterAmenities}
              onAmenitiesChange={setFilterAmenities}
              selectedPrice={filterPrice}
              onPriceChange={setFilterPrice}
              onReset={resetAdvancedFilters}
              activeCount={advancedFilterCount}
            />
            {/* View Mode Toggle */}
            <div className="flex items-center bg-stone-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === "grid" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <i className="ri-grid-line"></i> Liste
              </button>
              <button
                onClick={() => { setViewMode("map"); setMapSelected(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === "map" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <i className="ri-map-2-line"></i> Harita
              </button>
            </div>
          </div>
        </div>

        {/* Results bar */}
        <div className="flex flex-col gap-3 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-500">
              <strong className="text-stone-900">{filtered.length}</strong> işletme
              {viewMode === "grid" && totalPages > 1 && (
                <span className="ml-1">· Sayfa <strong className="text-stone-900">{currentPage}</strong>/{totalPages}</span>
              )}
              {viewMode === "map" && <span className="ml-2 text-xs text-amber-600 font-medium">· Harita görünümü</span>}
            </p>
            <Link to="/hotel-apply"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line"></i>
              İşletmenizi Ekleyin
            </Link>
          </div>
          {/* Active filter tags */}
          {(advancedFilterCount > 0 || filterNewOnly || filterRegion !== "all") && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-stone-400">Aktif filtreler:</span>
              {filterRegion !== "all" && (
                <span className="flex items-center gap-1.5 bg-stone-900 text-white text-xs px-3 py-1 rounded-full font-medium">
                  <i className="ri-map-2-line text-xs"></i>
                  {REGIONS.find((r) => r.value === filterRegion)?.label}
                  <button onClick={() => setFilterRegion("all")} className="cursor-pointer hover:text-stone-300">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {filterNewOnly && (
                <span className="flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs px-3 py-1 rounded-full font-medium">
                  <i className="ri-sparkling-line text-xs"></i> Yeni Üyeler
                  <button onClick={() => setFilterNewOnly(false)} className="cursor-pointer hover:text-emerald-600">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {filterPrice !== "all" && (
                <span className="flex items-center gap-1.5 bg-amber-100 text-amber-800 text-xs px-3 py-1 rounded-full font-medium">
                  {PRICE_RANGES.find((p) => p.value === filterPrice)?.label}
                  <button onClick={() => setFilterPrice("all")} className="cursor-pointer hover:text-amber-600">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {filterAmenities.map((am) => (
                <span key={am} className="flex items-center gap-1.5 bg-stone-100 text-stone-700 text-xs px-3 py-1 rounded-full font-medium">
                  {am}
                  <button onClick={() => setFilterAmenities((prev) => prev.filter((x) => x !== am))} className="cursor-pointer hover:text-stone-500">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
              <button onClick={() => { resetAdvancedFilters(); setFilterNewOnly(false); setFilterRegion("all"); }} className="text-xs text-rose-500 hover:text-rose-600 font-medium cursor-pointer whitespace-nowrap">
                Tümünü Temizle
              </button>
            </div>
          )}
        </div>

        {/* MAP VIEW */}
        {viewMode === "map" && (
          <MapView
            accommodations={filtered}
            selected={mapSelected}
            onSelect={setMapSelected}
            onContact={setReservationModal}
          />
        )}

        {/* GRID VIEW */}
        {viewMode === "grid" && (
          <>
            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-stone-300 text-4xl"></i>
                </div>
                <p className="text-stone-400">Arama kriterlerinize uygun işletme bulunamadı.</p>
              </div>
            ) : (
              <>
                {/* ── Gold Öne Çıkanlar Bölümü ── */}
                {goldItems.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-xl px-4 py-2">
                        <i className="ri-vip-crown-fill text-white text-sm"></i>
                        <span className="text-white text-sm font-bold tracking-wide">Öne Çıkan İşletmeler</span>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-amber-200 to-transparent"></div>
                      <span className="text-xs text-amber-600 font-semibold bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                        Gold Üye · Güvenilir & Onaylı
                      </span>
                    </div>
                    <div className="divide-y divide-amber-50">
                      {goldItems.map((acc) => (
                        <div key={acc.id} className="group flex items-center gap-5 py-4 hover:bg-amber-50/50 px-2 transition-colors">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-vip-crown-fill text-amber-500 text-sm"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${typeColors[acc.businessType] || "bg-stone-100 text-stone-600"}`}>
                                {typeLabels[acc.businessType] || acc.businessType}
                              </span>
                              <span className="text-xs text-stone-400">{acc.city} · {acc.district}</span>
                              {acc.starRating && (
                                <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold">
                                  <i className="ri-star-fill text-amber-400 text-xs"></i> {acc.starRating} Yıldız
                                </span>
                              )}
                            </div>
                            <Link to={`/accommodation/${acc.id}`} className="font-bold text-stone-900 text-base hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                              {acc.businessName}
                            </Link>
                            <p className="text-stone-500 text-xs line-clamp-1 mt-0.5">{acc.shortDesc}</p>
                            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                              <span className="text-xs font-bold text-amber-600">{acc.priceRange.split(" ")[0]}</span>
                              {acc.amenities?.slice(0, 3).map((a) => (
                                <span key={a} className="text-xs text-stone-400">· {a}</span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {acc.phone && (
                              <a href={`tel:${acc.phone}`} onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 px-3 py-1.5 text-emerald-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap hover:text-emerald-600">
                                <i className="ri-phone-fill text-emerald-600"></i> Ara
                              </a>
                            )}
                            <button onClick={() => setReservationModal(acc)}
                              className="flex items-center gap-1 px-3 py-1.5 text-amber-600 text-xs font-semibold cursor-pointer whitespace-nowrap hover:text-amber-700">
                              <i className="ri-calendar-check-line"></i> Rezervasyon
                            </button>
                          </div>
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-300 group-hover:text-amber-500 transition-colors">
                            <i className="ri-arrow-right-s-line"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                    {regularItems.length > 0 && (
                      <div className="flex items-center gap-3 mt-6 mb-4">
                        <div className="flex-1 h-px bg-stone-100"></div>
                        <span className="text-xs text-stone-400 font-medium px-3">Diğer İşletmeler</span>
                        <div className="flex-1 h-px bg-stone-100"></div>
                      </div>
                    )}
                  </div>
                )}

                <div className="divide-y divide-stone-100">
                  {regularItems.map((acc) => {
                    const isNew = acc.appliedAt
                      ? (Date.now() - new Date(acc.appliedAt).getTime()) < 30 * 24 * 60 * 60 * 1000
                      : false;
                    return (
                      <div key={acc.id} className="group flex items-center gap-5 py-4 hover:bg-stone-50 px-2 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${typeColors[acc.businessType] || "bg-stone-100 text-stone-600"}`}>
                              {typeLabels[acc.businessType] || acc.businessType}
                            </span>
                            <span className="text-xs text-stone-400">{acc.city} · {acc.district}</span>
                            {acc.starRating && (
                              <span className="flex items-center gap-0.5 text-xs text-stone-500">
                                <i className="ri-star-fill text-amber-400 text-xs"></i> {acc.starRating} Yıldız
                              </span>
                            )}
                            {isNew && (
                              <span className="text-xs text-emerald-600 font-medium">Yeni Eklendi</span>
                            )}
                          </div>
                          <Link to={`/accommodation/${acc.id}`} className="font-bold text-stone-900 text-base hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {acc.businessName}
                          </Link>
                          <p className="text-stone-500 text-xs line-clamp-1 mt-0.5">{acc.shortDesc}</p>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="text-xs font-bold text-amber-600">{acc.priceRange.split(" ")[0]}</span>
                            {acc.amenities?.slice(0, 3).map((a) => (
                              <span key={a} className="text-xs text-stone-400">· {a}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {acc.phone && (
                            <a href={`tel:${acc.phone}`} onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 px-3 py-1.5 text-emerald-700 text-xs font-semibold cursor-pointer whitespace-nowrap hover:text-emerald-600">
                              <i className="ri-phone-fill text-emerald-600"></i> Ara
                            </a>
                          )}
                          <button onClick={() => setReservationModal(acc)}
                            className="flex items-center gap-1 px-3 py-1.5 text-amber-600 text-xs font-semibold cursor-pointer whitespace-nowrap hover:text-amber-700">
                            <i className="ri-calendar-check-line"></i> Rezervasyon
                          </button>
                        </div>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-300 group-hover:text-amber-500 transition-colors">
                          <i className="ri-arrow-right-s-line"></i>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line"></i> Önceki
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-medium transition-all cursor-pointer ${
                            currentPage === page
                              ? "bg-stone-900 text-white"
                              : "border border-stone-200 text-stone-600 hover:bg-stone-50"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                    >
                      Sonraki <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Free membership CTA */}
          <div className="bg-stone-50 rounded-2xl p-7 flex flex-col justify-between gap-4 border border-stone-100">
            <div>
              <div className="w-10 h-10 flex items-center justify-center bg-stone-200 rounded-xl mb-4">
                <i className="ri-building-2-line text-stone-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Ücretsiz Üyelik
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                İşletmenizi platforma ekleyin, binlerce gezgine ulaşın. Komisyon yok, tamamen ücretsiz.
              </p>
            </div>
            <Link to="/hotel-apply"
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-building-2-line"></i>
              Ücretsiz Başvur
            </Link>
          </div>

          {/* Gold membership CTA */}
          <div className="relative rounded-2xl p-7 flex flex-col justify-between gap-4 overflow-hidden border border-amber-300" style={{ background: "linear-gradient(135deg, #1c1917 0%, #292524 100%)" }}>
            <div className="absolute inset-0 opacity-10">
              <img src="https://readdy.ai/api/search-image?query=gold%20luxury%20texture%20abstract%20warm%20amber%20shimmer%20premium%20background&width=600&height=300&seq=gold-cta-bg&orientation=landscape" alt="" className="w-full h-full object-cover object-top" />
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-400 rounded-xl">
                  <i className="ri-vip-crown-fill text-white text-xl"></i>
                </div>
                <span className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Gold</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Gold Üyelik
              </h3>
              <p className="text-stone-300 text-sm leading-relaxed">
                Altın rozet, liste başı sıralama ve öne çıkan etiketi ile rakiplerinizden ayrışın. 3x daha fazla görüntülenme.
              </p>
            </div>
            <Link to="/gold-apply"
              className="relative flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-vip-crown-fill"></i>
              Gold Üyelik Talep Et
            </Link>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {contactModal && (
        <ContactModal accommodation={contactModal} onClose={() => setContactModal(null)} />
      )}

      {/* Reservation Modal */}
      {reservationModal && (
        <ReservationModal accommodation={reservationModal} onClose={() => setReservationModal(null)} />
      )}

      <Footer />
    </div>
  );
};

export default AccommodationsPage;
